import { useState } from "react";
import { Button, Icon } from "hireart-components";
import PropTypes from "prop-types";
import { Modal } from "semantic-ui-react";
import { Toasts } from "common/components/Toasts";
import NetPromoterScoreForm from "./NetPromoterScoreForm";

import "./net-promoter-score-modal.scss";

const NetPromoterScoreModal = ({
  hasCloseButton = true,
  hasSurveyEnded = false,
  hasOverlay = false,
  initialComment = "",
  initialScore,
  isOpen = false,
  onOpen = () => {},
  onClose = () => {},
  onSubmit,
  referralGroups,
  wasSubmitted = false,
}) => {
  const [hasSubmitted, setHasSubmitted] = useState(wasSubmitted);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedScore, setSelectedScore] = useState(initialScore);
  const [comment, setComment] = useState(initialComment);

  const handleChange = (e) => {
    setComment(e.target.value);
  };

  const handleSelectScore = (score) => {
    setSelectedScore(score);
  };

  const handleSubmit = () => {
    setIsSubmitting(true);

    return onSubmit({ score: selectedScore, comment: comment.trim() })
      .then(() => {
        setHasSubmitted(true);
        setIsSubmitting(false);
      })
      .catch(() => {
        setIsSubmitting(false);
        Toasts.error(
          "There was an error sending this request. Our engineering team has been notified of this error."
        );
      });
  };

  const isDisabled =
    selectedScore === undefined || isSubmitting || hasSubmitted;
  const isExpired = hasSurveyEnded && !hasSubmitted;

  return (
    <Modal
      className={`nps-modal ${hasSubmitted ? "nps-modal-submitted" : ""} 
        ${isExpired ? "nps-modal-expired" : ""}`}
      closeOnDimmerClick={false}
      dimmer={hasOverlay}
      open={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      id="nps-modal"
    >
      <Modal.Header>
        <img
          className="nps-modal-logo"
          src="/assets/net-promoter-score-modal/hireart-logo.svg"
          alt="HireArt logo"
        />
        {hasCloseButton && (
          <button
            className="nps-modal-close-button"
            id="nps-modal-close-button"
            type="button"
            onClick={onClose}
          >
            <Icon type="close" />
          </button>
        )}
        {!hasSurveyEnded && !hasSubmitted && (
          <h2 className="nps-modal-heading">How's HireArt Doing?</h2>
        )}
      </Modal.Header>
      <Modal.Content>
        <div className="nps-modal-content-wrapper">
          {isExpired && (
            <>
              <span className="nps-modal-emoji">😅</span>
              <h2 className="nps-modal-heading">This Link Is Expired</h2>
              <p>
                There's nothing for you to do here right now — we appreciate you
                helping us make HireArt even better!
              </p>
            </>
          )}
          {!hasSurveyEnded && !hasSubmitted && (
            <NetPromoterScoreForm
              comment={comment}
              hasSubmitted={hasSubmitted}
              selectedScore={selectedScore}
              onChange={handleChange}
              onSelectScore={handleSelectScore}
              referralGroups={referralGroups}
            />
          )}
          {hasSubmitted && (
            <>
              <span className="nps-modal-emoji">✅</span>
              <h2 className="nps-modal-heading">Thanks For Your Feedback!</h2>
              <p>
                We appreciate the time you took to provide your input and will
                work to make HireArt even better for you.
              </p>
            </>
          )}
        </div>
      </Modal.Content>
      <Modal.Actions>
        {!hasSurveyEnded && !hasSubmitted && (
          <Button size="lg" disabled={isDisabled} fluid onClick={handleSubmit}>
            Submit
          </Button>
        )}
        {(hasSubmitted || isExpired) && (
          <Button size="lg" fluid onClick={onClose}>
            Close
          </Button>
        )}
      </Modal.Actions>
    </Modal>
  );
};

NetPromoterScoreModal.propTypes = {
  hasCloseButton: PropTypes.bool,
  hasOverlay: PropTypes.bool,
  hasSurveyEnded: PropTypes.bool,
  isOpen: PropTypes.bool,
  initialComment: PropTypes.string,
  initialScore: PropTypes.number,
  onClose: PropTypes.func,
  onOpen: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  referralGroups: PropTypes.string.isRequired,
  wasSubmitted: PropTypes.bool,
};

export default NetPromoterScoreModal;
